<template>
  <o-dialog
    ref="dialog"
    :title="$t('pass.newCustomer')"
    :info="info"
    :options="options"
    :loading="loading"
    :portal="portal"
  >
    <!--<div v-if="customerPass" class="mt-4">
      <pass-select v-model="customerPass.pass" />
    </div>-->

    <div v-if="customerPass" class="pt-4 text-left">
      <o-form ref="form">
        <div class="pt-4 md:pt-6">
          <o-field-group
            v-slot="{ status }"
            :label="$t('general.pass')"
            :name="$t('general.pass')"
            class="block mb-3"
            rules="required"
          >
            <pass-select v-model="customerPass.pass" :status="status" />
          </o-field-group>

          <o-field-group
            v-slot="{ status }"
            :label="$t('general.type')"
            :name="$t('general.type')"
            class="mb-4 block"
            rules="required"
          >
            <segment-field v-model="type" :options="passTypeOptions" :status="status" />
          </o-field-group>

          <div v-if="isAssign" class="p-3 bg-gray-50 rounded-md mb-3 dark:bg-gray-900">
            <o-field-group
              v-slot="{ status }"
              :label="$t('general.customer')"
              :name="$t('general.customer')"
              class="block"
              rules="required"
            >
              <customer-select v-model="customerPass.customer" preview create default-status-class="bg-white h-10 dark:bg-gray-900" :status="status" />
            </o-field-group>
          </div>

          <div v-if="isGift" class="p-3 bg-gray-50 rounded-md mb-3 dark:bg-gray-900">
            <o-field-group
              v-slot="{ status }"
              :label="$t('general.senderName')"
              :name="$t('general.senderName')"
              class="mb-4 block"
              rules="required"
            >
              <text-field v-model="customerPass.gift.from.name" :status="status" />
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.recipientName')"
              :name="$t('general.recipientName')"
              class="mb-4 block"
              rules="required"
            >
              <text-field v-model="customerPass.gift.to.name" :status="status" />
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.recipientEmail')"
              :name="$t('general.recipientEmail')"
              class="mb-4 block"
              rules="required|email"
            >
              <text-field v-model="customerPass.gift.to.email" :status="status" />
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.message')"
              :name="$t('general.message')"
              class="mb-4 block"
              rules="required"
            >
              <text-area-field v-model="customerPass.gift.message" :status="status" />
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.deliveryDate')"
              :name="$t('general.deliveryDate')"
              date
              class="block text-left"
              rules="required"
              :message="deliveryDateLabel"
            >
              <date-field
                v-model="customerPass.gift.deliveryDate"
                :augmenter="Augmenters.timestamp"
                :status="status"
                :placeholder="$t('datetime.labels.today')"
                :min-date="new Date()"
              />
            </o-field-group>
          </div>
        </div>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { CustomerPass } from '~/_api/models/customerPass'

import PassSelect from '@/components/pass/elements/Select.vue'
import CustomerSelect from '@/components/customer/elements/Select.vue'
import { TextField, CurrencyField, DateField, SegmentField, TextAreaField } from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'update:customerPass', customerPass: any): void
  (e: 'update:order', order: any): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()
const { createOrder } = useOrderCreate()

const dialog = ref<any>()
const customerPass = ref<any>(null)
const order = ref<any>(null)
const customer = ref<any>(null)
const type = ref('assign')
const passTypeOptions = computed(() => [
  { label: $i18n.t('general.assign'), value: 'assign' },
  { label: $i18n.t('general.gift'), value: 'gift' },
])

const info = computed(() => {
  return type.value === 'gift' ? $i18n.t('components.customerPass.actions.add.wontLink') : $i18n.t('components.customerPass.actions.add.willLink')
})

const options = computed(() => {
  if (order.value) {
    return [
      { text: $i18n.t('general.cancel'), type: 'cancel' },
      { text: $i18n.t('pass.add'), type: 'confirm', variant: 'primary', callback: () => confirm(false) },
    ]
  }

  return [
    { text: $i18n.t('general.cancel'), type: 'cancel' },
    { text: $i18n.t('pass.add'), type: 'confirm', variant: 'primary', callback: () => confirm(false) },
    { text: $i18n.t('components.customerPass.actions.add.addAndPay'), type: 'confirm', variant: 'primary', callback: () => confirm(true) },
  ]
})

const deliveryDateLabel = computed(() => {
  if (customerPass.value?.gift?.deliveryDate) {
    return $i18n.t('components.customerPass.actions.add.deliveryDateLabelWithDate', { date: (useNuxtApp().$date as any).format(customerPass.value.gift.deliveryDate * 1000, (useNuxtApp().$date as any).presets.long) })
  }

  return $i18n.t('components.customerPass.actions.add.deliveryDateLabel')
})

const isGift = computed(() => {
  return type.value === 'gift'
})

const isAssign = computed(() => {
  return type.value === 'assign'
})

const open = ({ customer: customerArg, order: orderArg }: { customer?: any; order?: any } = {}) => {
  const cp = new CustomerPass()
  cp.customer = customerArg
  cp.gift = {
    to: {
      name: '',
      email: '',
    },
    from: {
      name: '',
    },
    message: '',
    deliveryDate: null,
  }

  customerPass.value = cp
  order.value = orderArg
  customer.value = customerArg

  dialog.value.open()
}

const confirm = async (payNow = false) => {
  emit('update:loading', true)

  try {
    if (isGift.value) {
      customerPass.value.customer = null
    }

    if (isAssign.value) {
      customerPass.value.gift = null
    }

    const response = await $api.customerPass.save(customerPass.value)
    const cp = response.data

    if (cp) {
      emit('success')
      emit('update:customerPass', cp)

      $notification.toast({
        title: $i18n.t('components.customerPass.actions.add.success'),
        text: $i18n.t('components.customerPass.actions.add.successInfo'),
        variant: 'success',
      })

      $bus.emit.created('CustomerPass', cp)

      if (order.value) {
        const orderResponse = await $api.order.addCustomerPasses(String(order.value.idOrder), [String(cp.idCustomerPass)])
        const orderResult = orderResponse.data

        if (orderResult) {
          emit('update:order', orderResult)
        }
      }

      if (payNow) {
        const items = [cp]
        const orderCustomer = cp.customer
        await createOrder({ items, customer: orderCustomer })
      }

      emit('update:loading', false)

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
